import { useTranslation } from 'react-i18next';
import { useNotifications } from '@otp/ui';
import { ResourceListItem } from '../../../types';
import { useMutation } from '@apollo/client';
import {
  CreateResourceFileBookmarkDocument,
  DeleteResourceFileBookmarkDocument,
  ResourcesDocument,
  UserResourcesDocument,
} from 'src/graphql/generated/graphql';

type ActionsColumnProps = {
  resource: ResourceListItem;
};

export const useBookmarks = ({ resource }: ActionsColumnProps) => {
  const notifications = useNotifications();
  const [addBookmark] = useMutation(CreateResourceFileBookmarkDocument, {
    refetchQueries: [ResourcesDocument, UserResourcesDocument],
  });
  const [deleteBookmark] = useMutation(DeleteResourceFileBookmarkDocument, {
    refetchQueries: [ResourcesDocument, UserResourcesDocument],
  });

  const { t } = useTranslation();

  const onBookmarkAdd = async ({
    organizationId,
  }: { organizationId?: string } = {}) => {
    try {
      await addBookmark({
        variables: {
          data: {
            pageId: resource.id,
            organizationId,
          },
        },
      });

      notifications.show(t('resources.bookmarks.added-successfully'));
    } catch (e) {
      notifications.show(t('resources.bookmarks.adding-error'), {
        severity: 'error',
      });
    }
  };

  const onBookmarkRemove = async (id: string) => {
    try {
      await deleteBookmark({
        variables: {
          id,
        },
      });

      notifications.show(t('resources.bookmarks.removed-successfully'));
    } catch (e) {
      notifications.show(t('resources.bookmarks.removing-error'), {
        severity: 'error',
      });
    }
  };

  const personalBookmark = resource.bookmarks?.find(
    (bookmark) => !bookmark.organizationId,
  );
  const teamBookmark = resource.bookmarks?.find(
    (bookmark) => bookmark.organizationId,
  );

  return {
    personalBookmark,
    teamBookmark,
    onBookmarkRemove,
    onBookmarkAdd,
  };
};
